import React, { memo, useState } from 'react';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { InlineUnDecoratedTextInput } from 'src/components/general/inputs/inline-text-inputs';

const TitleText = styled.button<{ $editable: boolean }>`
  background: transparent;
  border: 2px solid transparent;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.carbon};
  cursor: ${({ $editable }) => ($editable ? 'text' : 'default')};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 16px;
  letter-spacing: -0.48px;
  height: 34px;
  line-height: 22px;
  max-width: 500px;
  overflow: hidden;
  padding: 4px 3px;
  width: 100%;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: ${({ $editable, theme }) =>
      $editable ? theme.colors.porcelain : 'transparent'};
  }
`;

const TitleInput = styled(InlineUnDecoratedTextInput)`
  max-width: 500px;
  width: 100%;

  input {
    color: ${({ theme }) => theme.colors.carbon};
    font-size: 16px;
    height: 34px;
    letter-spacing: -0.48px;
    padding-left: 3px;

    ${up('md')} {
      height: 34px;
      padding-left: 3px;
    }
  }
`;

type EditableTitleProps = {
  value: string;
  editable: boolean;
  startEditing?: boolean;
  onSave: (value: string) => void;
  onCancel?: VoidFunction;
  testId: string;
};

export const EditableTitle = memo<EditableTitleProps>(
  ({ value, editable, startEditing = false, onSave, onCancel, testId }) => {
    const [draft, setDraft] = useState<string | null>(
      editable && startEditing ? value : null,
    );

    const finish = () => {
      const nextValue = draft?.trim() ?? '';

      setDraft(null);

      if (nextValue && nextValue !== value) {
        onSave(nextValue);
      } else {
        onCancel?.();
      }
    };

    if (draft === null) {
      return (
        <TitleText
          type="button"
          data-lgg-id={testId}
          $editable={editable}
          onClick={() => editable && setDraft(value)}
        >
          {value}
        </TitleText>
      );
    }

    return (
      <TitleInput
        autoFocus
        data-lgg-id={`${testId}-input`}
        value={draft}
        onFocus={(event) => event.target.select()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={finish}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            finish();
          }

          if (event.key === 'Escape') {
            setDraft(null);
            onCancel?.();
          }
        }}
      />
    );
  },
);
